// Unified Google connections: one hook for every scope (company-wide, per
// project, and the signed-in user's own account).
//
// Replaces the split between useProjectMeetAccount and useUserMeetAccount for
// the settings screen. Those hooks still exist and still work — the meetings
// UI reads them to gate scheduling — but connecting now goes through here.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { crmApi } from "../api/client";

const KEY = ["crm", "google-connections"];

// projectIds is part of the key because the server only returns the projects
// we ask about — a different set is a genuinely different result.
export function useGoogleConnections(projectIds = []) {
  const ids = [...projectIds].map(String).sort();
  return useQuery({
    queryKey: [...KEY, ids.join(",")],
    queryFn: async () => {
      const { data } = await crmApi.get("/google-connections", {
        params: ids.length ? { projectIds: ids.join(",") } : undefined,
      });
      return data?.connections || [];
    },
    staleTime: 30_000,
  });
}

// Returns the Google consent URL. The caller navigates the CURRENT tab to it —
// a popup can't close itself after a cross-origin redirect, which always left
// users on a dead "you may close this window" page.
export function useStartGoogleConnect() {
  return useMutation({
    mutationFn: async ({ scope, projectId, capabilities = [] }) => {
      const { data } = await crmApi.get("/auth/google/connections/start", {
        params: {
          scope,
          ...(projectId ? { projectId } : {}),
          ...(capabilities.length ? { capabilities: capabilities.join(",") } : {}),
        },
      });
      return data;
    },
  });
}

export function useDisconnectGoogle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ scope, projectId }) => (
      await crmApi.delete("/google-connections", {
        params: { scope, ...(projectId ? { projectId } : {}) },
      })
    ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
      // The older per-scope queries back the meetings UI's "can I schedule?"
      // gate, so they have to refetch too or scheduling stays enabled against
      // an account that no longer exists.
      qc.invalidateQueries({ queryKey: ["crm", "user-meet-account"] });
      qc.invalidateQueries({ queryKey: ["crm", "project-meet-account"] });
      qc.invalidateQueries({ queryKey: ["crm", "lead-meetings"] });
    },
  });
}
